import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';

import { ReactComponent as EyeIcon } from 'src/assets/icons/eye.svg';
import { ReactComponent as HealthMonitorIcon } from 'src/assets/icons/healthMonitor.svg';
import LogoIcon from 'src/assets/icons/logoNavBar.png';
import { ReactComponent as PlayIcon } from 'src/icons/play.svg';
import { ReactComponent as SettingsIcon } from 'src/icons/settings-1.svg';
import { ReactComponent as UserIcon } from 'src/icons/user.svg';
// Navigation icons - using new icons from src/icons folder
import { ReactComponent as DashboardIcon } from 'src/icons/home.svg';
import { ReactComponent as PatientsIcon } from 'src/icons/user-group.svg';
import { ReactComponent as ScansIcon } from 'src/icons/scan.svg';
import { ReactComponent as AnalyticsIcon } from 'src/icons/sliders.svg';

import { ReactComponent as UploadIcon } from 'src/icons/upload.svg';
import { ReactComponent as GetStartedIcon } from 'src/icons/reading-mode.svg';
// Chat nav — will be implemented soon:
// import { ReactComponent as ChatIcon } from 'src/icons/chat.svg';
import { ReactComponent as ArrowRightIcon } from 'src/icons/arrow-forward-simple.svg';
import { ReactComponent as MenuIcon } from 'src/icons/menu.svg';
import { ReactComponent as SunIcon } from 'src/icons/star.svg';
import { ReactComponent as MoonIcon } from 'src/icons/star.svg';
import { UserSelectors } from 'src/reducers/user';
import { clearAll } from 'src/services/sessionStorage';
import { Size } from 'src/shared/text';
import { clearAuthTokens } from 'src/utils/auth';
import { Colors } from 'src/utils/color';
import styled from 'styled-components';

import {
    ChangePasswordModal,
    HealthMonitorModal,
    UploadVideoModal
} from './components';
import {
    Content,
    ContentWrapper,
    EmptyView,
    IconButton,
    IconStyle,
    Icons,
    Item,
    LeftBarContainer,
    Menu,
    NavIconWrapper,
    PlayIconWrapper,
    RightNavWrapper,
    SettingsContent,
    SettingsHeader,
    SettingsItem,
    SettingsSidebar,
    Text,
    Topbar,
    TopbarLeft,
    TopbarTitle,
    TopbarSubtitle,
    TopbarRight,
    ThemeToggleButton,
    Wrapper,
    // New unified sidebar components
    UnifiedSidebar,
    SidebarHeader,
    SidebarBrand,
    BrandIcon,
    BrandText,
    BrandTitle,
    BrandSubtitle,
    SidebarContent,
    NavigationSection,
    SectionTitle,
    NavigationItem,
    NavigationText,
    UserSection,
    UserInfo,
    UserAvatar,
    UserDetails,
    UserName,
    UserRole,
    UserOrganization,
    SignOutButton,
    SignOutText,
    ToggleButton
} from './styles';
import { isCLightUser } from 'src/utils/utils';

type Props = {
    children?: React.ReactNode;
};

const menuOption = [
    'Account',
    'Notification',
    'Help center',
    'Change your password'
];

// Navigation data
const navigationItems = [
    { id: 'dashboard', label: 'Dashboard', icon: DashboardIcon, path: '/retina', count: null },
    { id: 'patients', label: 'Patients', icon: PatientsIcon, path: '/patients', count: null },
    { id: 'scans', label: 'Scan Analysis', icon: ScansIcon, path: '/scans', count: null },
    { id: 'analytics', label: 'Analytics', icon: AnalyticsIcon, path: '/analytics', count: null },
    { id: 'upload', label: 'Upload', icon: UploadIcon, path: '/retina/upload', count: null },
    { id: 'get-started', label: 'Get Started', icon: GetStartedIcon, path: '/get-started', count: null },
    // Chat — will be implemented soon (restore ChatIcon import above):
    // { id: 'chat', label: 'Chat', icon: ChatIcon, path: '/chat', count: null },
];

const settingsItems = [
    { id: 'settings', label: 'Settings', icon: SettingsIcon, path: '/settings' },
];

const DashboardWrapper: FunctionComponent<Props> = ({ children }: Props) => {
    const navigate = useNavigate();
    const modalRef = useRef<any>(null);
    const settingRef = useRef<any>(null);
    const healthModalRef = useRef<any>(null);
    const [menu, setMenu] = useState<boolean>(false);
    const [settingMenu, setSettingMenu] = useState<boolean>(false);
    // Load sidebar state from localStorage, default to true if not set
    const [sidebarOpen, setSidebarOpen] = useState<boolean>(() => {
        const saved = localStorage.getItem('sidebarOpen');
        return saved !== null ? saved === 'true' : true;
    });
    // Theme mode state (day/night)
    const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
        const saved = localStorage.getItem('darkMode');
        return saved !== null ? saved === 'true' : false;
    });
    const location = useLocation();
    const [csvLink] = useState<string>(
        'https://sample-videos.com/csv/Sample-Spreadsheet-10-rows.csv'
    );
    const { profile } = UserSelectors();
    const isClightUser = isCLightUser(profile);
    
    // Debug: log profile data
    useEffect(() => {
        if (profile) {
            // console.log('User Profile:', profile);
            // console.log('Group Name:', profile.group_name);
            // console.log('Group ID:', profile.group_id);
        }
    }, [profile]);

    const handleLogoClick = useCallback(() => {
        navigate('/retina');
    }, [navigate]);

    const handleSidebarToggle = useCallback(() => {
        const newState = !sidebarOpen;
        setSidebarOpen(newState);
        // Persist sidebar state to localStorage
        localStorage.setItem('sidebarOpen', String(newState));
    }, [sidebarOpen]);

    const handleThemeToggle = useCallback(() => {
        const newMode = !isDarkMode;
        setIsDarkMode(newMode);
        localStorage.setItem('darkMode', String(newMode));
        // TODO: Apply theme changes to the app
    }, [isDarkMode]);

    const handleNavigationClick = useCallback((path: string, itemId: string) => {
        // Only navigate - let useEffect sync activeItem from pathname
        // This prevents flickering from state conflicts
        navigate(path);
    }, [navigate]);

    // sync active item with current route so highlight is correct on refresh/deeplinks
    // useMemo to avoid recreating sorted items on every render
    const sortedNavigationItems = useMemo(() => {
        return [...navigationItems].sort((a, b) => b.path.length - a.path.length);
    }, []);

    // Compute activeItem directly from pathname - no state needed, prevents flickering
    const activeItem = useMemo(() => {
        const match = sortedNavigationItems.find(n => location.pathname === n.path || location.pathname.startsWith(n.path + '/'));
        return match ? match.id : 'dashboard';
    }, [location.pathname, sortedNavigationItems]);

    // brand logo component that tints the logo to our theme blue using CSS mask
    const BrandLogo = styled.div<{ color?: 'primary' | 'black' }>`
        width: 217px;
        height: 47px;
        background-color: ${p => p.color === 'black' ? Colors.extra.black : Colors.theme.primary};
        -webkit-mask: url(${LogoIcon}) no-repeat center;
        mask: url(${LogoIcon}) no-repeat center;
        -webkit-mask-size: contain;
        mask-size: contain;
        cursor: pointer;
    `;

    const handleSignOut = useCallback(() => {
        clearAuthTokens();
        clearAll();
        navigate('/');
    }, [navigate]);

    const onSettingItemClick = useCallback(
        (option: string) => {
            setSettingMenu(false);
            if (option === 'Change your password') {
                settingRef?.current?.show();
            }
        },
        [settingRef]
    );

    const tooltip = (label: string) => {
        return <Tooltip id="tooltip">{label}</Tooltip>;
    };


    return (
        <>
            <Wrapper>
                {/* Topbar with title and theme toggle */}
                <Topbar sidebarOpen={sidebarOpen}>
                    <TopbarLeft>
                        <TopbarTitle>RetiOne Clinical Platform</TopbarTitle>
                        <TopbarSubtitle>Clinical Analysis Pipeline</TopbarSubtitle>
                    </TopbarLeft>
                    <TopbarRight>
                        <ThemeToggleButton onClick={handleThemeToggle} title={isDarkMode ? 'Switch to Day Mode' : 'Switch to Night Mode'}>
                            {isDarkMode ? <SunIcon /> : <MoonIcon />}
                        </ThemeToggleButton>
                    </TopbarRight>
                </Topbar>
                
                {/* Sidebar and content sit side-by-side so the sidebar takes space */}
                <ContentWrapper>
                    <UnifiedSidebar isOpen={sidebarOpen}>
                    <SidebarHeader>
                        {sidebarOpen ? (
                            <>
                                <SidebarBrand>
                                    <BrandLogo color={'primary'} onClick={handleLogoClick} />
                                </SidebarBrand>
                                <ToggleButton onClick={handleSidebarToggle} title="Collapse sidebar">
                                    <MenuIcon />
                                </ToggleButton>
                            </>
                        ) : (
                            <ToggleButton onClick={handleSidebarToggle} title="Expand sidebar">
                                <MenuIcon />
                            </ToggleButton>
                        )}
                    </SidebarHeader>
                    
                    <SidebarContent>
                        {sidebarOpen ? (
                            <>
                                <NavigationSection>
                                    <SectionTitle>Main navigation</SectionTitle>
                            {navigationItems.map((item) => {
                                const IconComponent = item.icon;
                                return (
                                    <NavigationItem
                                        key={item.id}
                                        active={activeItem === item.id}
                                        onClick={() => handleNavigationClick(item.path, item.id)}
                                        collapsed={false}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%' }}>
                                            <IconComponent style={{ width: '20px', height: '20px' }} />
                                            <NavigationText>{item.label}</NavigationText>
                                            {item.count && (
                                                <span style={{ 
                                                    marginLeft: 'auto', 
                                                    fontSize: '12px', 
                                                    color: Colors.extra.dark_gray 
                                                }}>
                                                    {item.count}
                                                </span>
                                            )}
                                        </div>
                                    </NavigationItem>
                                );
                            })}
                        </NavigationSection>
                        
                        <NavigationSection>
                            <SectionTitle>Account</SectionTitle>
                            {settingsItems.map((item) => {
                                const IconComponent = item.icon;
                                return (
                                    <NavigationItem
                                        key={item.id}
                                        active={activeItem === item.id}
                                        onClick={() => handleNavigationClick(item.path, item.id)}
                                        collapsed={false}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%' }}>
                                            <IconComponent style={{ width: '20px', height: '20px' }} />
                                            <NavigationText>{item.label}</NavigationText>
                                        </div>
                                    </NavigationItem>
                                );
                            })}
                                </NavigationSection>
                            </>
                        ) : (
                            <NavigationSection>
                                {navigationItems.map((item) => {
                                    const IconComponent = item.icon;
                                    return (
                                        <NavigationItem
                                            key={item.id}
                                            active={activeItem === item.id}
                                            onClick={() => handleNavigationClick(item.path, item.id)}
                                            title={item.label}
                                            collapsed={true}
                                        >
                                            <IconComponent style={{ width: '20px', height: '20px' }} />
                                        </NavigationItem>
                                    );
                                })}
                                {settingsItems.map((item) => {
                                    const IconComponent = item.icon;
                                    return (
                                        <NavigationItem
                                            key={item.id}
                                            active={activeItem === item.id}
                                            onClick={() => handleNavigationClick(item.path, item.id)}
                                            title={item.label}
                                            collapsed={true}
                                        >
                                            <IconComponent style={{ width: '20px', height: '20px' }} />
                                        </NavigationItem>
                                    );
                                })}
                            </NavigationSection>
                        )}
                    </SidebarContent>
                    
                    {sidebarOpen ? (
                        <UserSection>
                            <UserInfo>
                                <UserAvatar>
                                    {profile?.first_name?.[0] || profile?.username?.[0] || 'U'}
                                    {profile?.last_name?.[0] || ''}
                                </UserAvatar>
                                <UserDetails>
                                    <UserName>
                                        {profile?.first_name && profile?.last_name 
                                            ? `${profile.first_name} ${profile.last_name}`
                                            : profile?.username || 'User'}
                                    </UserName>
                                    <UserRole>Clinician</UserRole>
                                    {profile?.group_name ? (
                                        <UserOrganization>Organization: {profile.group_name}</UserOrganization>
                                    ) : profile?.group_id ? (
                                        <UserOrganization>Organization: Group {profile.group_id}</UserOrganization>
                                    ) : null}
                                </UserDetails>
                            </UserInfo>
                            <SignOutButton onClick={handleSignOut}>
                                <SignOutText>
                                    Sign Out <ArrowRightIcon style={{ width: '12px', height: '12px' }} />
                                </SignOutText>
                            </SignOutButton>
                        </UserSection>
                    ) : (
                        <UserSection>
                            <UserAvatar style={{ margin: '0 auto' }}>
                                {profile?.first_name?.[0] || profile?.username?.[0] || 'U'}
                                {profile?.last_name?.[0] || ''}
                            </UserAvatar>
                        </UserSection>
                    )}
                    </UnifiedSidebar>
                    {/* Remove old thin icon bar; show main content to the right of sidebar */}
                    <Content sidebarOpen={sidebarOpen}>{children}</Content>
                </ContentWrapper>
            </Wrapper>
            <UploadVideoModal
                ref={modalRef}
                onHide={() => modalRef?.current?.hide()}
            />
            <HealthMonitorModal ref={healthModalRef} />
            <ChangePasswordModal
                ref={settingRef}
                onSave={(data) => {
                    settingRef?.current?.hide();
                }}
            />
        </>
    );
};

export default DashboardWrapper;
